import { useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent } from "./components/ui/card";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Toaster, toast } from "sonner";
import { Eye, CheckCircle, DollarSign, Phone, Mail, MapPin, CalendarIcon } from "lucide-react";
import { format, isFriday } from "date-fns";
import axios from "axios";
import PrivacyPolicy from "./PrivacyPolicy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_shine-hero/artifacts/u84wdb4s_HHGC%20%20%28Logo%29.png";

// Header Component
const Header = () => (
  <header className="header" data-testid="header">
    <div className="header-content">
      <img 
        src={LOGO_URL} 
        alt="Headlight Hero GC" 
        className="header-logo"
        data-testid="header-logo"
      />
    </div>
  </header>
);

// Hero Section
const Hero = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero" data-testid="hero-section">
      <div className="hero-content">
        <h1 className="hero-title" data-testid="hero-title">SEE CLEARLY. DRIVE SAFELY.</h1>
        <p className="hero-tagline" data-testid="hero-tagline">Your headlights. Restored.</p>
        <p className="hero-subtitle" data-testid="hero-subtitle">
          Professional headlight restoration on the Gold Coast.
        </p>
        <Button 
          onClick={scrollToContact}
          className="cta-button"
          data-testid="book-service-btn"
        >
          BOOK A SERVICE
        </Button>
      </div>
    </section>
  );
};

// Why Restore Section
const WhyRestore = () => {
  const benefits = [
    {
      icon: <Eye className="benefit-icon" />,
      title: "BETTER VISIBILITY",
      description: "Crystal clear headlights improve your vision at night by up to 50%"
    },
    {
      icon: <CheckCircle className="benefit-icon" />,
      title: "PASS YOUR ROADWORTHY",
      description: "Cloudy headlights can fail roadworthy inspections — we fix that fast"
    },
    {
      icon: <DollarSign className="benefit-icon" />,
      title: "SAVE MONEY",
      description: "Restoration costs a fraction of replacing your headlights"
    }
  ];

  return (
    <section className="why-restore" data-testid="why-restore-section">
      <h2 className="section-title">WHY RESTORE?</h2>
      <div className="benefits-grid">
        {benefits.map((benefit, index) => (
          <Card key={index} className="benefit-card" data-testid={`benefit-card-${index}`}>
            <CardContent className="benefit-content">
              <div className="icon-wrapper">
                {benefit.icon}
              </div>
              <h3 className="benefit-title">{benefit.title}</h3>
              <p className="benefit-description">{benefit.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

// Our Service Section
const OurService = () => (
  <section className="our-service" data-testid="our-service-section">
    <h2 className="section-title">OUR SERVICE</h2>
    <p className="service-description">
      We restore foggy, yellowed, or hazy headlights back to crystal clear — quickly and affordably. 
      Serviced at our Gold Coast workshop — drop your vehicle in and we'll have it done fast. Mobile callout available on request.
    </p>
  </section>
);

// Pricing Section
const Pricing = () => (
  <section className="pricing" data-testid="pricing-section">
    <h2 className="section-title">PRICING</h2>
    <Card className="pricing-card" data-testid="pricing-card">
      <CardContent className="pricing-content">
        <div className="price-amount">$150</div>
        <div className="price-label">PER PAIR + GST</div>
        <p className="price-note">Includes professional restoration of both headlights</p>
      </CardContent>
    </Card>
  </section>
);

// Testimonials Section
const Testimonials = () => {
  const testimonials = [
    {
      // Single combined before/after image with labels already in the image
      combinedImage: "https://customer-assets.emergentagent.com/job_shine-hero/artifacts/r3gvfg66_Dad%20Car.png",
      quote: "Couldn't believe the difference! My headlights look brand new and I passed my roadworthy with no issues.",
      author: "Sarah M.",
      location: "Burleigh Heads"
    },
    {
      combinedImage: "https://customer-assets.emergentagent.com/job_shine-hero/artifacts/xyvy0ew1_ADil%20Car.png",
      quote: "Quick, professional service at my home. The visibility at night is so much better now. Highly recommend!",
      author: "Mike T.",
      location: "Southport"
    }
  ];

  return (
    <section className="testimonials" data-testid="testimonials-section">
      <h2 className="section-title">RESULTS &amp; REVIEWS</h2>
      <div className="testimonials-grid">
        {testimonials.map((testimonial, index) => (
          <Card key={index} className="testimonial-card" data-testid={`testimonial-card-${index}`}>
            <CardContent className="testimonial-content">
              {testimonial.combinedImage ? (
                <div className="combined-image-container">
                  <img 
                    src={testimonial.combinedImage} 
                    alt="Before and after headlight restoration" 
                    className="combined-before-after-image"
                  />
                </div>
              ) : (
                <div className="before-after-container">
                  <div className="before-after-item">
                    <img 
                      src={testimonial.beforeImage} 
                      alt="Before restoration" 
                      className="before-after-image"
                    />
                    <span className="before-after-label">BEFORE</span>
                  </div>
                  <div className="before-after-item">
                    <img 
                      src={testimonial.afterImage} 
                      alt="After restoration" 
                      className="before-after-image"
                    />
                    <span className="before-after-label">AFTER</span>
                  </div>
                </div>
              )}
              <div className="testimonial-text-content">
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <p className="testimonial-author">{testimonial.author}</p>
                <p className="testimonial-location">{testimonial.location}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    suburb: '',
    preferred_time: ''
  });
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Function to check if a date is a Friday
  const isNotFriday = (date) => {
    return !isFriday(date);
  };

  // Function to disable past dates and non-Fridays
  const disabledDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || isNotFriday(date);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (date) {
      setFormData({ ...formData, preferred_time: format(date, "EEEE, d MMMM yyyy") });
    }
    setCalendarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast.error("Please select a Friday for your appointment");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.status === 'success') {
        toast.success("Thank you! We'll be in touch soon.");
        setFormData({ name: '', phone: '', suburb: '', preferred_time: '' });
        setSelectedDate(null);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="contact" data-testid="contact-section">
      <h2 className="section-title">BOOK A SERVICE</h2>
      <p className="contact-subtitle">Gold Coast &amp; surrounding areas</p>
      
      <div className="contact-container">
        <Card className="contact-form-card" data-testid="contact-form-card">
          <CardContent className="contact-form-content">
            <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
              <div className="form-group">
                <Label htmlFor="name" className="form-label">NAME</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                  className="form-input"
                  data-testid="input-name"
                />
              </div>
              
              <div className="form-group">
                <Label htmlFor="phone" className="form-label">PHONE</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Your phone number"
                  className="form-input"
                  data-testid="input-phone"
                />
              </div>
              
              <div className="form-group">
                <Label htmlFor="suburb" className="form-label">SUBURB</Label>
                <Input
                  id="suburb"
                  name="suburb"
                  value={formData.suburb}
                  onChange={handleChange}
                  required
                  placeholder="Your suburb"
                  className="form-input"
                  data-testid="input-suburb"
                />
              </div>
              
              <div className="form-group">
                <Label className="form-label">SELECT A FRIDAY</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="date-picker-button"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="date-picker-icon" />
                      {selectedDate ? format(selectedDate, "EEEE, d MMMM yyyy") : "Choose a Friday"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="calendar-popover" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={disabledDays}
                      className="friday-calendar"
                      data-testid="friday-calendar"
                    />
                    <p className="calendar-hint">Only Fridays are available for booking</p>
                  </PopoverContent>
                </Popover>
              </div>
              
              <Button 
                type="submit" 
                disabled={loading}
                className="submit-button"
                data-testid="submit-btn"
              >
                {loading ? 'SENDING...' : 'SEND ENQUIRY'}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        <div className="contact-info" data-testid="contact-info">
          <div className="contact-item">
            <Phone className="contact-icon" />
            <a href="tel:+61416630605" className="contact-link">+61 416 630 605</a>
          </div>
          <div className="contact-item">
            <Mail className="contact-icon" />
            <a href="mailto:headlightherogc@gmail.com" className="contact-link">headlightherogc@gmail.com</a>
          </div>
          <div className="contact-item">
            <MapPin className="contact-icon" />
            <span className="contact-text">Gold Coast &amp; surrounding areas</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="footer-content">
      <img 
        src={LOGO_URL} 
        alt="Headlight Hero GC" 
        className="footer-logo"
        data-testid="footer-logo"
      />
      <p className="footer-text">HEADLIGHT HERO GC</p>
      <p className="footer-location">Gold Coast, QLD</p>
      <Link to="/privacy-policy" className="footer-privacy-link">Privacy Policy</Link>
    </div>
  </footer>
);

// Home Page Component
const HomePage = () => (
  <>
    <Header />
    <main>
      <Hero />
      <WhyRestore />
      <OurService />
      <Testimonials />
      <Pricing />
      <Contact />
    </main>
    <Footer />
  </>
);

function App() {
  return (
    <div className="app" data-testid="app">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
